/**
 * ⛔⛔ THE ADAPTER BETWEEN THE CEO PAYLOAD AND THE SCREEN — Card P151.
 *
 * W3's P149 (#732) replaces every scalar on `reports:ceo:run` with an
 * envelope — `{value, previous, delta, deltaPct, unit, currency, tracked,
 * reason, provisional}` — and adds a period request and an `activePipeline`
 * block. This module reads that.
 *
 * ⚠⚠ AND IT STILL READS THE OLD SHAPE, DELIBERATELY. #732 is not merged. If
 * this screen only understood the new payload, merging it first would blank
 * the live CEO dashboard until W3 landed — and merge order between two
 * windows is not something a screen should depend on. So: new shape when
 * `metrics` is present, legacy fields otherwise, and a test drives BOTH.
 *
 * ⛔ The legacy path yields `tracked: true` with `previous: null`, which is
 * honest: the old server genuinely has no prior-period figure, so no Δ chip
 * renders rather than a fabricated one.
 */

/** W3 resolves these server-side from the same table as src/lib/easyReports.js. */
export const PERIOD_PRESETS = Object.freeze({
  Today: 'today',
  Week: 'this_week',
  Month: 'this_month',
  Quarter: 'this_quarter',
  Year: 'this_year',
});

/** The request body for a period label. `null` ⇒ all time (today's behaviour). */
/**
 * The request envelope for the chosen window.
 *
 * ⭐⭐ ONE SHAPE FOR BOTH KINDS — Card P185. A preset sends `{preset}`; a custom
 * range sends `{from, to}`. Both are already understood by
 * routes/_ceo_dashboard.js (resolveWindow) and routes/_finance_summary.js
 * (resolvePeriod), so a custom range reuses the period plumbing rather than
 * opening a second path to the same numbers — which is how a dashboard ends up
 * with two answers to one question.
 *
 * ⚠ ISO strings on the wire, built from the BUSINESS-time range. The boundary
 * decision is made once, in customRange(), and this never re-derives it.
 */
export function presetRequest(period) {
  if (period && typeof period === 'object' && period.custom) {
    return { from: period.from, to: period.to };
  }
  const preset = PERIOD_PRESETS[period];
  return preset ? { preset } : {};
}

/**
 * The single source of truth for "which window is the dashboard showing".
 *
 * ⭐ EXPOSED FOR W1 (P185 item 4). The AI report must take its period from this
 * same state, so a custom range flows into the report without W1
 * re-implementing the parse, the validation or the business-time boundary. One
 * getter; a second implementation would be a second answer.
 */
export function rangeOf(period) {
  if (period && typeof period === 'object' && period.custom) {
    return { kind: 'custom', from: period.from, to: period.to, label: period.label, preset: null };
  }
  return { kind: 'preset', from: null, to: null, label: period, preset: PERIOD_PRESETS[period] || null };
}

/** An empty envelope: not tracked, with a reason. Never a zero. */
export function untracked(reason) {
  return {
    value: null, previous: null, delta: null, deltaPct: null,
    unit: 'count', currency: null, tracked: false,
    reason: reason || 'unavailable', provisional: false,
  };
}

/*
 * ⛔⛔ P161 item 4 — 'currency' NAMED NO SCALE, and the two senders disagreed.
 * The server sent CENTS from _finance_summary.js and DOLLARS from
 * _ceo_dashboard.js under that one label; read with the wrong assumption a
 * figure is off by exactly 100×, and BOTH readings render a plausible number.
 *
 * The server now refuses to emit it (routes/_ceo_metric.js). This side still
 * ACCEPTS it, deliberately and only on the legacy scalar path: a legacy
 * payload predates the fix and its money has always been dollars, so mapping
 * it to 'dollars' is a statement of what it already was, not a conversion.
 * ⚠ Nothing here multiplies or divides — a scale bug that reached a formatter
 * would be silent, where a mislabelled envelope is at least inspectable.
 */
export const MONEY_UNITS = Object.freeze(['cents', 'dollars']);
const legacyUnit = (unit) => (unit === 'currency' ? 'dollars' : unit);

/** Wrap a legacy scalar as an envelope, without inventing a comparison. */
function legacy(value, unit = 'count') {
  if (value === null || value === undefined) return untracked('not_reported');
  const u = legacyUnit(unit);
  return {
    value, previous: null, delta: null, deltaPct: null,
    unit: u, currency: MONEY_UNITS.includes(u) ? 'USD' : null,
    tracked: true, reason: null, provisional: false,
  };
}

/**
 * Read one metric by key, from either payload shape.
 *
 * ⛔⛔ `value: null` and `value: 0` are DIFFERENT FACTS and this never
 * conflates them. `null` + `tracked:false` is "we cannot see this"; `0` is
 * "we looked and it is zero". The live dashboard's three zeros — therapies
 * scheduled, Lead→Therapy, the 60-day forecast — were all the first kind
 * presented as the second.
 */
export function metric(data, key, legacyPath, unit = 'count') {
  if (!data) return untracked('loading');
  if (data.metrics && Object.prototype.hasOwnProperty.call(data.metrics, key)) {
    const m = data.metrics[key];
    if (!m) return untracked('not_reported');
    // ⚠ Trust `tracked`, not truthiness of `value`: a tracked 0 is a real zero.
    return { ...m, tracked: m.tracked !== false && m.value !== null && m.value !== undefined };
  }
  if (typeof legacyPath === 'function') return legacy(legacyPath(data), unit);
  return untracked('not_reported');
}

/** The state a card should render for an envelope. */
export function cardState(m, { loading = false, failed = false } = {}) {
  if (failed) return 'failed';
  if (loading || !m) return 'loading';
  if (m.reason === 'loading') return 'loading';
  return m.tracked ? 'ok' : 'untracked';
}

/**
 * ⚠ REASON CODES ARE NOT PROSE. The server sends machine codes
 * (`no_stripe_credential`); a screen that printed them verbatim would show a
 * reader a snake_case token. Unknown codes fall back to the code itself
 * rather than a generic shrug — an unrecognised reason is still information,
 * and inventing "unavailable" for it would hide a new server state.
 */
const REASON_TEXT = Object.freeze({
  loading: null,
  not_reported: 'This figure is not in the current server response.',
  no_stripe_credential: 'No payment credential is configured.',
  no_succeeded_charges: 'No successful payments have been recorded yet.',
  stripe_error: 'The payment provider could not be reached.',
  unavailable: 'Not available.',
});

export function reasonText(m) {
  if (!m || m.tracked) return null;
  const code = m.reason || 'unavailable';
  return REASON_TEXT[code] || code;
}

/**
 * The period range label, for the Δ chip's caption.
 * ⚠ Falls back to a neutral phrase rather than naming a window the server
 * did not actually use.
 */
export function previousLabel(data) {
  const prev = data && data.range && data.range.previous;
  return (prev && prev.label) || 'vs previous';
}

/**
 * ⭐⭐ Stages with no bucket-map row. W3 counts them as ACTIVE and reports
 * them rather than dropping them — silently excluding what cannot be
 * classified is how a pipeline total shrinks for reasons nobody can see.
 *
 * The screen surfaces the names so the ruling that fixes them is obviously
 * outstanding, not buried in a payload.
 */
export function unmappedStages(data) {
  const ap = data && data.activePipeline;
  if (!ap) return null;
  const names = Array.isArray(ap.unmappedStages) ? ap.unmappedStages : [];
  const count = Number.isFinite(ap.unmappedStagesCount) ? ap.unmappedStagesCount : names.length;
  if (!count) return null;
  return { count, names };
}


/**
 * ⭐⭐ THE FINANCE PERIOD ENVELOPE — Card P158, on #744's contract.
 *
 * `reports:finance:summary` now takes the same `{from,to,preset}` as
 * `reports:ceo:run` and returns `summary.period.*` in the same envelope
 * shape as `data.metrics.*`, plus `summary.range` and `summary.pointInTime`.
 *
 * ⚠⚠ TWO CLOCKS ON ONE SCREEN, AND CONFLATING THEM IS THE WHOLE RISK.
 * `period.*` are TOTALS FOR THE SELECTED WINDOW; `overdue` and
 * `deposits.outstanding` are a SNAPSHOT AS OF NOW and do not move with the
 * period switch at all. Shown side by side without saying so, a reader takes
 * "Overdue $1.39M" as "overdue this month", which it is not and never was.
 * The server names them in `pointInTime.appliesTo`; the screen repeats it.
 */

/** Read one `summary.period.*` figure as a card-ready envelope. */
export function financeMetric(summary, key) {
  if (!summary || summary.tracked === false) {
    return untracked(summary && summary.reason ? summary.reason : 'loading');
  }
  const m = summary.period && summary.period[key];
  if (m) return { ...m, tracked: m.tracked !== false && m.value !== null && m.value !== undefined };

  /*
   * ⚠⚠ A SERVER WITHOUT #744 SENDS NO `period` BLOCK AT ALL, and the
   * all-time totals it does send are the only figures that exist. Returning
   * "not tracked" there would blank a working card the moment this merged
   * ahead of the server — the same merge-order trap P151 avoided, one
   * contract later. The legacy figure is shown, and `financeRangeLabel`
   * says "All time" so the words match the number.
   */
  const legacyAllTime = {
    collected: summary.collected && summary.collected.cents,
    invoiced: summary.booked && summary.booked.cents,
  }[key];
  if (legacyAllTime === undefined || legacyAllTime === null) return untracked('not_reported');
  return {
    value: legacyAllTime, previous: null, delta: null, deltaPct: null,
    /*
     * ⛔⛔ 'cents', NOT 'currency' — AND THIS ONE WAS A 100× LANDMINE.
     *
     * The values above are `summary.collected.cents` and `summary.booked.cents`:
     * CENTS. Tagged 'currency' they rendered correctly ONLY because #742's call
     * site passes formatCents explicitly; anything reading the envelope's own
     * unit — CeoOverview's `show()`, for one — maps 'currency' to dollars and
     * would print $477,530,900 where $4,775,309 belongs. Both are believable on
     * this dashboard, which is exactly why P161 item 4 made 'currency'
     * unexpressible on the server. Found when #742 and #746 merged: neither
     * branch was wrong on its own.
     */
    unit: 'cents', currency: 'USD', tracked: true, reason: null,
    previousReason: 'all_time_range_has_no_prior_window', provisional: false,
  };
}

/**
 * The window label for a finance card.
 * ⛔ From the SERVER's range, never a hard-coded "All time" — the label and
 * the figures must come from the same decision about which window was used.
 */
export function financeRangeLabel(summary) {
  if (!summary || summary.tracked === false) return null;
  const r = summary.range;
  /* ⚠ No range block ⇒ a pre-#744 server, whose figures ARE all-time. */
  if (!r) return 'All time';
  return r.label || (r.from ? 'Selected period' : 'All time');
}

/** The comparison caption, or null when the server says there is no prior window. */
export function financePreviousLabel(summary, m) {
  if (!m || m.previous === null || m.previous === undefined) return null;
  const r = summary && summary.range;
  return (r && r.previous) ? 'vs previous period' : 'vs previous';
}

/**
 * ⛔⛔ WHY A COMPARISON IS ABSENT, in the server's words.
 *
 * `previousReason: "all_time_range_has_no_prior_window"` is a FACT about the
 * question, not a gap in the data: all time has nothing before it. Rendering
 * "+100%" there — which is what a naive (cur-prev)/prev over prev=0 produces
 * — would invent growth out of the absence of a comparison.
 */
const PREVIOUS_REASON_TEXT = Object.freeze({
  all_time_range_has_no_prior_window: 'No prior window — this is an all-time figure.',
  snapshot_has_no_prior_window: 'A snapshot, not a period total.',
});

export function previousReasonText(m) {
  if (!m || m.previous !== null && m.previous !== undefined) return null;
  const code = m.previousReason;
  if (!code) return null;
  return PREVIOUS_REASON_TEXT[code] || code;
}

/** Figures that are "as of now" regardless of the period switch. */
export function pointInTimeNote(summary) {
  const p = summary && summary.pointInTime;
  if (!p) return null;
  return { asOf: p.asOf, note: p.note, appliesTo: Array.isArray(p.appliesTo) ? p.appliesTo : [] };
}
